// Column-mapping for bulk lead upload. The backend (yandle_bulk_lead_processor)
// only recognizes a fixed set of header names (contact_name/name,
// contact_email/email, contact_number/phone/phone_number, ...) -- any other
// header silently becomes a custom field instead of landing on the field it
// was actually meant for (this is exactly what happened to a 106-row upload
// that used "Phone No" instead of "Contact Number": every row failed with
// "contact_number is mandatory" even though the phone number was right
// there). This module lets the UI auto-suggest a mapping from the file's own
// headers to the org's real lead fields, so that mismatch gets caught and
// fixed before upload instead of after every row fails.

export const SYSTEM_FIELD_LABELS = {
  contact_name: 'Contact Name',
  contact_email: 'Contact Email',
  contact_number: 'Contact Number',
  type_of_business: 'Business Type',
  industry: 'Industry',
  message: 'Message',
  comments: 'Comments',
}

// Only used to SUGGEST a default mapping -- never blocks a manual choice.
const ALIASES = {
  contact_name: ['name', 'first name', 'contact name', 'full name', 'lead name', 'customer name'],
  contact_email: ['email', 'email address', 'email id', 'mail', 'mail id'],
  contact_number: [
    'phone', 'phone no', 'phone number', 'mobile', 'mobile no', 'mobile number',
    'contact no', 'contact number', 'whatsapp', 'whatsapp number', 'cell', 'cell number',
  ],
  type_of_business: ['business type', 'type of business'],
  industry: ['industry'],
  message: ['message', 'note', 'notes'],
  comments: ['comment', 'comments', 'remark', 'remarks'],
}

// The single field the backend actually requires on every row (matches
// yandle_bulk_lead_processor's own rule -- only contact_number is mandatory).
export const REQUIRED_TARGET_FIELD = 'contact_number'

function normalizeText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Best-guess target field key for a raw column header, or null if nothing
 * matches closely enough. Only suggests fields actually present (enabled)
 * in the selected product(s), so it never proposes a field the org doesn't
 * have.
 */
export function suggestFieldForHeader(header, targetFields) {
  const norm = normalizeText(header)
  if (!norm) return null

  for (const field of targetFields) {
    if (normalizeText(field.label) === norm || normalizeText(field.name) === norm) {
      return field.name
    }
  }

  const targetKeys = new Set(targetFields.map((field) => field.name))
  for (const [key, aliases] of Object.entries(ALIASES)) {
    if (!targetKeys.has(key)) continue
    if (aliases.some((alias) => normalizeText(alias) === norm)) return key
  }

  return null
}

/** Parse a browser File (.csv/.xls/.xlsx) into a 2D array [headerRow, ...dataRows]. */
export async function parseSpreadsheetToRows(file) {
  const XLSX = await import('xlsx')
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array' })
  const sheetName = workbook.SheetNames[0]
  if (!sheetName) throw new Error('The file has no worksheets.')

  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
    header: 1,
    defval: '',
    raw: false,
  })
  if (!Array.isArray(rows) || rows.length < 2) {
    throw new Error('File needs a header row and at least one data row.')
  }
  return rows
}

/**
 * Rebuild the file as CSV text, renaming each mapped column's header to the
 * real field key it was mapped to (contact_name, contact_number, or a
 * custom field's own key) -- everything else passes through with its
 * original header, exactly the pre-mapping "unmatched columns become custom
 * fields" behavior.
 *
 * `mapping` is { [headerIndex]: targetFieldKey | null }.
 */
export async function buildMappedCsv(rows, mapping) {
  const XLSX = await import('xlsx')
  const headerRow = rows[0].map((header, idx) => mapping[idx] || header)
  const sheet = XLSX.utils.aoa_to_sheet([headerRow, ...rows.slice(1)])
  return XLSX.utils.sheet_to_csv(sheet)
}

/** Merge enabled field definitions across the selected products (contexts), first-seen wins. */
export function mergeTargetFields(contexts, selectedContextNames) {
  const merged = new Map()
  for (const name of selectedContextNames) {
    const context = contexts.find((item) => String(item?.context_name ?? '').trim() === name)
    const definitions = context?.custom_field_definitions
    if (!definitions || typeof definitions !== 'object') continue

    for (const [fieldName, definition] of Object.entries(definitions)) {
      if (definition?.enabled === false) continue
      if (merged.has(fieldName)) continue
      merged.set(fieldName, {
        name: fieldName,
        label: String(definition?.label ?? '').trim() || SYSTEM_FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' '),
        isUniqueIdentifier: definition?.is_unique_identifier === true,
      })
    }
  }
  return [...merged.values()]
}

/**
 * Builds and downloads a starter .xlsx for the given target fields --
 * exactly the same list mergeTargetFields() produces for the currently
 * selected product(s), so the template's columns are always guaranteed to
 * match what the mapping step above actually expects. contact_number's
 * column comes first (it's the one field every row must have); the rest
 * follow in whatever order the product defines them.
 */
export async function downloadLeadTemplate(targetFields, { productLabel = '' } = {}) {
  const XLSX = await import('xlsx')

  const ordered = [...targetFields].sort((a, b) => {
    if (a.name === REQUIRED_TARGET_FIELD) return -1
    if (b.name === REQUIRED_TARGET_FIELD) return 1
    return 0
  })
  const headers = ordered.map((field) => field.label)

  const worksheet = XLSX.utils.aoa_to_sheet([headers])
  worksheet['!cols'] = headers.map(() => ({ wch: 22 }))

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads')

  const suffix = productLabel
    ? `_${productLabel.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}`
    : ''
  XLSX.writeFile(workbook, `lead_upload_template${suffix}.xlsx`)
}
